import { ReactElement } from 'react';
import { Outlet, useParams } from 'react-router';
import {
    UIButton,
    UIInlineAlert,
    UISpacer,
    UISpinner,
    UISubNav,
    UIText
} from '@/ui';
import { CompanyUser } from '@/api';
import { MemberProvider, useCompanyContext } from '@/companies/providers';
import { useFetchCompanyUser } from '@/companies/queries';
import { useResendInviteAction } from '@/companies/actions';
import { usePagesTranslation } from '../hooks';
import { useFormatDate } from '@/common/hooks';

export function MemberIdSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { company } = useCompanyContext();
    const { memberId } = useParams();

    const { user, status, error, canRetry, refetch } = useFetchCompanyUser(
        memberId ?? ''
    );

    // A user who hasn't accepted their invite yet has no name, so fall back to
    // their email — the same as the members list rows do.
    const name = user?.joined
        ? `${user.firstName} ${user.lastName}`
        : (user?.email ?? '');

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: 32,
                gap: 24
            }}
        >
            <UISubNav
                title={name}
                subTitle={user?.joined ? user.email : undefined}
                // An invited user has no name, so the title is already their
                // email — say who invited them instead of repeating it.
                subContent={
                    user && !user.joined ? (
                        <InvitedSubContent user={user} />
                    ) : undefined
                }
                img={{
                    img: user?.image ?? '',
                    alt: name
                }}
                // Only meaningful while the invite is outstanding. Whether this
                // viewer may actually resend it is the button's own business —
                // it disables itself and says why.
                rightItems={
                    user && !user.joined ? (
                        <ResendInviteButton user={user} />
                    ) : undefined
                }
                links={[
                    {
                        text: t('shared.labels.general'),
                        link: 'general',
                        additionalActiveLinkPaths: [
                            `/company/${company.id}/settings/members/${memberId ?? ''}`
                        ]
                    },
                    {
                        text: t('shared.labels.notifications'),
                        link: 'notifications'
                    },
                    {
                        text: t('shared.labels.emergency_info'),
                        link: 'emergency-information'
                    },
                    {
                        text: t('shared.labels.app_devices'),
                        link: 'app-devices'
                    }
                ]}
            />

            {status === 'pending' && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <UISpinner />
                </div>
            )}

            {status === 'error' && (
                <div>
                    <UIInlineAlert text={error} />
                    {canRetry && (
                        <>
                            <UISpacer />
                            <UIButton onClick={refetch} variant="primary">
                                <UIText>{t('shared.labels.retry')}</UIText>
                            </UIButton>
                        </>
                    )}
                </div>
            )}

            {/* The sub-pages take the user for granted, so they are only
                mounted once it has loaded. */}
            {user && (
                <MemberProvider companyUser={user}>
                    <Outlet />
                </MemberProvider>
            )}
        </div>
    );
}

/** Who invited this user, and when. Only shown before they have joined. */
function InvitedSubContent(props: { user: CompanyUser }): ReactElement {
    const { user } = props;

    const { t } = usePagesTranslation();
    const { formatDateOnly } = useFormatDate();

    const invitedBy = user.invited?.by;
    const name = invitedBy
        ? `${invitedBy.firstName} ${invitedBy.lastName}`
        : null;

    return (
        <UIText type="default2">
            <UIText bold type="link">
                {t('pages.member_id_settings.invited')}
            </UIText>
            {name
                ? ` ${t('pages.member_id_settings.invited_by', {
                      name,
                      date: formatDateOnly(new Date(user.invited?.at ?? ''))
                  })}`
                : ` ${t('pages.member_id_settings.invited_on', {
                      date: formatDateOnly(new Date(user.invited?.at ?? ''))
                  })}`}
        </UIText>
    );
}

/** Surfaced in the header so a pending invite can be chased without hunting. */
function ResendInviteButton(props: { user: CompanyUser }): ReactElement {
    const { user } = props;

    const { t } = usePagesTranslation();
    const resendInviteAction = useResendInviteAction({ user });

    return (
        <UIButton
            text={t('shared.labels.resend_invite')}
            leftIcon="send"
            loading={resendInviteAction.loading}
            disabled={!resendInviteAction.isAllowed}
            disabledTooltip={resendInviteAction.reason}
            onClick={() => {
                resendInviteAction.resendInvite().catch(() => {}); // Error handled in hook
            }}
        />
    );
}
